// DEBUG VERSION: src/pages/orders/OrdersPage.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../../components/layout/DashboardLayout";
import OrdersTable from "../../components/orders/OrdersTable";
import OrdersFilter from "../../components/orders/OrdersFilter";
import ProductRequestsTable from "../../components/orders/ProductRequestsTable";
import StatsCard from "../../components/dashboard/StatsCard";
import Button from "../../components/common/Button";
import { useAuth } from "../../contexts/AuthContext";
import { useNotification } from "../../contexts/NotificationContext";
import {
  getOrders,
  getOrderStatistics,
  updateOrderStatus,
} from "../../services/orderService";
import {
  getProductRequests,
  respondToProductRequest,
  getProductRequestStatistics
} from "../../services/productRequestService";

const OrdersPage = () => {
  const { vendorProfile } = useAuth();
  const { showAlert } = useNotification();

  // Debug state
  const [debugInfo, setDebugInfo] = useState({
    vendorId: null,
    hasOrderManagement: false,
    requestsLoaded: false,
    error: null
  });

  // State for orders
  const [orders, setOrders] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  // State for product requests
  const [productRequests, setProductRequests] = useState([]);
  const [productRequestsLoading, setProductRequestsLoading] = useState(true);
  const [productRequestStats, setProductRequestStats] = useState(null);
  const [activeTab, setActiveTab] = useState("product-requests"); // Start with product requests for debugging

  // Filters
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    dateRange: null,
  });

  // Pagination
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [total, setTotal] = useState(0);

  // Update debug info when vendor profile changes
  useEffect(() => {
    setDebugInfo(prev => ({
      ...prev,
      vendorId: vendorProfile?.uid || null,
      hasOrderManagement: vendorProfile?.businessType !== 'without-orders'
    }));
  }, [vendorProfile]);

  // Load orders
  const loadOrders = async (refresh = false) => {
    try {
      if (!vendorProfile?.uid) {
        console.log('No vendor UID available for loading orders');
        return;
      }

      setLoading(true);
      setError("");

      if (refresh) {
        setLastVisible(null);
        setPage(1);
      }

      const options = {
        pageSize,
        lastVisible: refresh ? null : lastVisible,
        status: filters.status || null,
        dateRange: filters.dateRange || null,
        search: filters.search || "",
      };

      const result = await getOrders(vendorProfile.uid, options);

      if (refresh) {
        setOrders(result.orders);
      } else {
        setOrders((prev) => [...prev, ...result.orders]);
      }

      setLastVisible(result.lastDoc);
      setTotal(result.total);
      setHasMore(result.orders.length === pageSize);
      setLoading(false);
    } catch (error) {
      console.error("Error loading orders:", error);
      setError("Failed to load orders. Please try again.");
      setLoading(false);
    }
  };

  // Load product requests with detailed debugging
  const loadProductRequests = async () => {
    try {
      if (!vendorProfile?.uid) {
        console.log('❌ No vendor UID available for loading product requests');
        setDebugInfo(prev => ({
          ...prev,
          error: 'No vendor UID available'
        }));
        setProductRequestsLoading(false);
        return;
      }

      console.log('🔄 Loading product requests for vendor:', vendorProfile.uid);
      setProductRequestsLoading(true);
      setDebugInfo(prev => ({
        ...prev,
        error: null
      }));

      // Load requests
      const requests = await getProductRequests(vendorProfile.uid);
      console.log('✅ Product requests loaded:', requests.length, requests);
      
      // Load stats
      const stats = await getProductRequestStatistics(vendorProfile.uid);
      console.log('✅ Product request stats loaded:', stats);
      
      setProductRequests(requests);
      setProductRequestStats(stats);
      setDebugInfo(prev => ({
        ...prev,
        requestsLoaded: true,
        error: null
      }));
      
    } catch (error) {
      console.error('❌ Error loading product requests:', error);
      setDebugInfo(prev => ({
        ...prev,
        error: error.message
      }));
      showAlert('Failed to load product requests: ' + error.message, 'error');
    } finally {
      setProductRequestsLoading(false);
    }
  };

  // Load order statistics
  const loadStatistics = async () => {
    try {
      if (!vendorProfile?.uid) return;

      const dateRange = filters.dateRange || null;
      const result = await getOrderStatistics(vendorProfile.uid, dateRange);
      setStatistics(result);
    } catch (error) {
      console.error("Error loading order statistics:", error);
    }
  };

  // Initial load
  useEffect(() => {
    console.log('🎯 OrdersPage useEffect triggered, vendorProfile:', vendorProfile);
    
    if (vendorProfile?.uid) {
      console.log('✅ Vendor profile available, loading data...');
      loadOrders(true);
      loadStatistics();
      loadProductRequests();
    } else {
      console.log('❌ No vendor profile available');
    }
  }, [vendorProfile]);

  // Reload when filters change
  useEffect(() => {
    if (vendorProfile?.uid) {
      loadOrders(true);
      loadStatistics();
    }
  }, [filters]);

  // Handle filter change
  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Load more orders
  const handleLoadMore = () => {
    if (hasMore && !loading) {
      setPage((prev) => prev + 1);
      loadOrders();
    }
  };

  // Handle order status update
  const handleStatusUpdate = async (orderId, newStatus, note = "") => {
    try {
      await updateOrderStatus(orderId, newStatus, { note });

      setOrders((prev) =>
        prev.map((order) => {
          if (order.id === orderId) {
            return { ...order, status: newStatus };
          }
          return order;
        })
      );

      loadStatistics();
      showAlert(`Order status updated to ${newStatus}`, "success");
    } catch (error) {
      console.error("Error updating order status:", error);
      showAlert("Failed to update order status", "error");
    }
  };

  // Handle product request response with debugging
  const handleProductRequestResponse = async (requestId, action, quantity = 0) => {
    try {
      console.log('🔄 Responding to product request:', { requestId, action, quantity });
      
      await respondToProductRequest(requestId, action, quantity);
      
      console.log('✅ Response successful, reloading requests...');
      
      // Reload product requests
      await loadProductRequests();
      
      showAlert(`Product request ${action}ed successfully`, "success");
    } catch (error) {
      console.error('❌ Error responding to product request:', error);
      showAlert("Failed to respond to product request: " + error.message, "error");
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Check if vendor has order management
  const hasOrderManagement = vendorProfile?.businessType !== 'without-orders';

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
       

        {/* Page Header */}
        <div className="py-4">
          <h1 className="text-2xl font-semibold text-gray-900">
            Orders & Product Requests
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your orders and respond to product requests from mechanics.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab("orders")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "orders"
                  ? "border-orange-500 text-orange-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Customer Orders
              {statistics && (
                <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs">
                  {statistics.totalOrders}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("product-requests")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "product-requests"
                  ? "border-orange-500 text-orange-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Product Requests
              {productRequestStats && productRequestStats.pendingRequests > 0 && (
                <span className="ml-2 bg-red-100 text-red-900 py-0.5 px-2.5 rounded-full text-xs">
                  {productRequestStats.pendingRequests}
                </span>
              )}
             
            </button>
          </nav>
        </div>

        {/* Statistics Cards */}
        {activeTab === "orders" && statistics && (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6">
            <StatsCard
              title="Total Orders"
              value={statistics.totalOrders}
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-orange-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                  />
                </svg>
              }
            />
            <StatsCard
              title="Total Revenue"
              value={formatCurrency(statistics.totalRevenue)}
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
            />
            <StatsCard
              title="Average Order Value"
              value={formatCurrency(statistics.averageOrderValue)}
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-indigo-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
              }
            />
            <StatsCard
              title="Pending Orders"
              value={
                statistics.statusCounts.Pending +
                statistics.statusCounts.Confirmed
              }
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-yellow-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
            />
          </div>
        )}

        {/* Product Request Statistics */}
        {activeTab === "product-requests" && productRequestStats && (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6">
            <StatsCard
              title="Total Requests"
              value={productRequestStats.totalRequests}
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-blue-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                  />
                </svg>
              }
            />
            <StatsCard
              title="Pending Requests"
              value={productRequestStats.pendingRequests}
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-yellow-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
            />
            <StatsCard
              title="Accepted Requests"
              value={productRequestStats.acceptedRequests}
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              }
            />
            <StatsCard
              title="Rejected Requests"
              value={productRequestStats.rejectedRequests}
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              }
            />
          </div>
        )}

        {/* Tab Content */}
        {activeTab === "orders" && (
          <>
            {/* Status Counts */}
            {statistics && (
              <div className="bg-white shadow rounded-md p-4 mb-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">
                  Order Status
                </h2>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-600">
                      {statistics.statusCounts.Pending}
                    </div>
                    <div className="text-sm text-gray-500">Pending</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">
                      {statistics.statusCounts.Confirmed}
                    </div>
                    <div className="text-sm text-gray-500">Confirmed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-indigo-600">
                      {statistics.statusCounts.Shipped}
                    </div>
                    <div className="text-sm text-gray-500">Shipped</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">
                      {statistics.statusCounts.Cancelled}
                    </div>
                    <div className="text-sm text-gray-500">Cancelled</div>
                  </div>
                </div>
              </div>
            )}

            {/* Filters */}
            <OrdersFilter filters={filters} onFilterChange={handleFilterChange} />

            {/* Orders Table */}
            <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-md">
              {error ? (
                <div className="p-4 text-red-700 bg-red-100 border border-red-400 rounded">
                  {error}
                </div>
              ) : (
                <OrdersTable
                  orders={orders}
                  loading={loading}
                  onStatusUpdate={handleStatusUpdate}
                />
              )}

              {/* Pagination */}
              <div className="px-4 py-3 border-t border-gray-200 sm:px-6">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-700">
                    Showing <span className="font-medium">{orders.length}</span> of{" "}
                    <span className="font-medium">{total}</span> orders
                  </div>

                  {hasMore && (
                    <Button
                      variant="secondary"
                      onClick={handleLoadMore}
                      disabled={loading || !hasMore}
                    >
                      {loading ? "Loading..." : "Load More"}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Product Requests Tab */}
        {activeTab === "product-requests" && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Product Requests from Mechanics
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Review and respond to product requests from mechanics in your network.
              </p>
              {debugInfo.error && (
                <div className="mt-2 p-2 bg-red-100 border border-red-300 rounded text-sm text-red-700">
                  Error: {debugInfo.error}
                </div>
              )}
            </div>
            <ProductRequestsTable
              requests={productRequests}
              loading={productRequestsLoading}
              onResponse={handleProductRequestResponse}
            />
            
           
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default OrdersPage;